import React from 'react'
import { Button, type ButtonProps, CircularProgress } from '@mui/material'

import { typography } from '~tokens/internal'
import { getLinkProps } from '~utils/navigationUtils'

import Icon, { type IconProps } from '../Icon'

import './Button.scss'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    link: true
  }
}

export interface StyledButtonProps extends Omit<
  ButtonProps,
  'loading' | 'loadingIndicator' | 'loadingPosition'
> {
  size?: 'small' | 'medium'
  variant?: 'contained' | 'outlined' | 'text' | 'link'
  width?: 'fit' | 'fill'
  target?: string
  rel?: string
  to?: string
  newTab?: boolean
  /** Shows a spinner before the label and disables the button while an action is in flight. */
  loading?: boolean
}

const DEFAULT_ICON_SIZE_PX: Record<NonNullable<StyledButtonProps['size']>, number> = {
  small: typography.fontSizePx.xs,
  medium: typography.fontSizePx.sm,
}

/**
 * Internal `Icon` elements set their size via inline styles, so the per-size CSS
 * defaults in Button.scss cannot reach them. Inject the default here unless the
 * caller passed an explicit `size` or `fontSize`. Call sites sometimes wrap the
 * icon (e.g. a clickable `Box` for a remove affordance) or render it inside the
 * button label, so recurse through wrappers to reach the nested `Icon`.
 */
const withDefaultIconSize = (icon: React.ReactNode, defaultSize: number): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon

  if (icon.type === Icon) {
    const { size, fontSize } = icon.props as IconProps
    return size === undefined && fontSize === undefined
      ? React.cloneElement(icon as React.ReactElement<IconProps>, { size: defaultSize })
      : icon
  }

  const { children } = icon.props as { children?: React.ReactNode }
  if (children === undefined) return icon

  return React.cloneElement(icon as React.ReactElement<{ children?: React.ReactNode }>, {
    children: React.Children.map(children, (child) => withDefaultIconSize(child, defaultSize)),
  })
}

const CustomButton = React.forwardRef<HTMLButtonElement, StyledButtonProps>(
  (
    {
      children,
      variant = 'contained',
      size = 'medium',
      color = 'primary',
      width,
      className,
      style,
      to,
      newTab = false,
      loading = false,
      disabled,
      startIcon,
      endIcon,
      ...props
    },
    ref,
  ) => {
    const defaultIconSize = DEFAULT_ICON_SIZE_PX[size]

    // Build className from props - all styling is handled by CSS classes
    const classNames = [
      'internal-button',
      `internal-button--${variant}`,
      `internal-button--${size}`,
      `internal-button--${color}`,
      width && `internal-button--width-${width}`,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    // Map 'link' to 'text' for MUI (link is our custom variant built on text)
    const muiVariant = variant === 'link' ? 'text' : variant

    const linkProps = getLinkProps(to, newTab)

    const iconSizedChildren = React.Children.map(children, (child) =>
      withDefaultIconSize(child, defaultIconSize),
    )
    const sizedChildren =
      variant === 'link' ? (
        <span className='internal-button__label'>{iconSizedChildren}</span>
      ) : (
        iconSizedChildren
      )

    return (
      <Button
        ref={ref}
        variant={muiVariant}
        size={size}
        className={classNames}
        style={style}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        startIcon={
          loading ? (
            <CircularProgress size={defaultIconSize} color='inherit' />
          ) : (
            withDefaultIconSize(startIcon, defaultIconSize)
          )
        }
        endIcon={withDefaultIconSize(endIcon, defaultIconSize)}
        {...props}
        {...linkProps}
      >
        {sizedChildren}
      </Button>
    )
  },
)

export default CustomButton
